import { isFunction, isUndefined } from '@rrc/utils'
// import device from '@rrc/device'
import { ISTEST, ISDEVELOPMENT } from '@/config/global.config'
import Vue from 'vue'
const ua = navigator.userAgent
const CUSTOM_PROTOCOL_SCHEME = 'rrche'
const QUEUE_HAS_MESSAGE = '__MESSAGE_MODEL__/'

const DEFAULT_OPTIONS = {
  appPrefix: 'renrenche'
}

function getNewVisitorUUID () {
  let UUID = (function(uuidRegEx, uuidReplacer) {
    return function() {
      return 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'.replace(uuidRegEx, uuidReplacer).toLowerCase()
    }
  }(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0,
      v = c == 'x' ? r : r & 3 | 8
    return v.toString(16)
  }))
  return UUID()
}

const EVENT_ERROR = 'error'

/**
 * 可供监听的事件
 * error 发生错误时调用，params: error
 */
export const EVENT_LIST = [EVENT_ERROR]

/**
 * RRC JS Bridge
 */
export default class Bridge {
  constructor (options = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options }
    this.isApp = new RegExp(this.options.appPrefix).test(ua)
    this.uniqueId = 1
    this.responseCallbacks = {}
    this.messagingIframe = null

    this._setup()

    // 观察者
    const observer = {}
    this.on = function (type, handler) {
      if (!EVENT_LIST.includes(type)) {
        return
      }

      const handlers = observer[type]

      if (handlers) {
        handlers.push(handler)
      } else {
        observer[type] = [handler]
      }
    }
    this.emit = function (type, ...args) {
      const handlers = observer[type] || []
      for (const handler of handlers) {
        handler(...args)
      }
    }
  }

  /**
   * 创建需要挂载到 window 上的 bridge 对象
   * @returns bridge
   * @memberof Bridge
   */
  _createBridgeObj () {
    return {
      send: this.send,
      _dispatchMessageFromNative: this._dispatchMessageFromNative.bind(this),
      _nativeDispatchToJS: this._nativeDispatchToJS.bind(this)

    }
  }

  /**
   * 初始化环境
   */
  _setup () {
    const bridgeObj = this._createBridgeObj()
    // register
    window.WebViewJavascriptBridge = bridgeObj

    // dispatch ready event
    const readyEvent = document.createEvent('Events')
    readyEvent.initEvent('WebViewJavascriptBridgeReady')
    readyEvent.bridge = bridgeObj
    document.dispatchEvent(readyEvent)
  }

  /**
   * app发送信息给js，提供给 APP 调用, 该方法主要用于app主动调js，而不是js请求app的回调的
   * @param {*} messageJSON 参数
   */

  _nativeDispatchToJS (messageJSON) {
    try {
      const messageObj = JSON.parse(messageJSON)
      if (messageObj) {
        Vue.prototype.$bus.$emit(messageObj.action, messageObj.data)
      } else {
        this.emit(EVENT_ERROR, {
          type: '返回数据为空',
          error: ''
        })
      }
    } catch (e) {
      this.emit(EVENT_ERROR, {
        type: '返回数据格式错误',
        error: e
      })
    }
  }

  /**
   * 提供给 APP 调用
   * @param {*} messageJSON 参数
   */
  _dispatchMessageFromNative (messageJSON) {
    const { responseCallbacks } = this
    setTimeout(() => {
      let message = ''
      let error = null
      try {
        message = JSON.parse(typeof messageJSON === 'string' ? messageJSON : JSON.stringify(messageJSON))
      } catch (e) {
        error = e
        this.emit(EVENT_ERROR, {
          type: 'json parse error in dispatch',
          error
        })
      }
      if (message.responseId) {
        const responseCallback = responseCallbacks[message.responseId]
        if (!responseCallback) {
          return
        }
        responseCallback(error, message.responseData)
        delete responseCallbacks[message.responseId]
      }
    }, 0)
  }

  _createBridge (url) {
    const messagingIframe = document.createElement('iframe')
    messagingIframe.style.display = 'none'
    messagingIframe.src = url
    document.documentElement.appendChild(messagingIframe)
    setTimeout(() => {
      messagingIframe.remove()
    }, 100)
  }

  /**
   * 向 APP 发起消息
   * @param {any} action 动作名
   * @param {any} data 参数 如果类型是function 则认为 是回调
   * @param {any} responseCallback 回调
   * @return {string} iframe.src
   * @memberof Bridge
   */
  send (action, data, responseCallback) {
    if (!this.isApp && !ISTEST) {
      return
    }

    // 如果是生产环境 让 openWindow 和 closeWindow 继续起作用
    if (ISDEVELOPMENT) {
      switch (action) {
        case 'openWindow':
          window.location.href = data.url
          return
        case 'closeWindow':
          window.router.go(-1)
          return
        default:
      }
    }

    let { uniqueId } = this
    const {
      responseCallbacks
    } = this
    const message = { action }

    if (!isUndefined(data) && !isFunction(data)) {
      message.data = data
    }

    if (isFunction(data)) {
      responseCallback = data
    }

    if (responseCallback) {
      const callbackId = `cb_${uniqueId++}_${new Date().getTime()}_${getNewVisitorUUID()}`
      responseCallbacks[callbackId] = responseCallback
      message.callbackId = callbackId
    }

    const messageQueueString = JSON.stringify(message)
    const url = `${CUSTOM_PROTOCOL_SCHEME}://${QUEUE_HAS_MESSAGE}${encodeURIComponent(messageQueueString)}`

    this._createBridge(url)

    if (ISTEST) {
      console.log(`${CUSTOM_PROTOCOL_SCHEME}://${QUEUE_HAS_MESSAGE}${messageQueueString}`) // eslint-disable-line
    }
  }

  /**
   *
   * 注入API 的方式调用
   * @param {*} action
   * @param {*} data
   * @returns
   * @memberof Bridge
   */
  nativeBridge (action, ...data) {
    if (!this.isApp && !ISTEST) {
      return undefined
    }
    if (window.RenrencheJSBridge || window.Android) {
      try {
        const par = { action, data: data[0] }
        const params = JSON.stringify(par)
        let response
        if (window.RenrencheJSBridge) {
          response = window.RenrencheJSBridge.nativeBridge(params)
          return JSON.parse(response)
        }
        if (window.Android) {
          // 客户端后期，统一改为RenrencheJSBridge调用方式
          // window.Android.tokenExpired()
          if (window.Android[action]) {
            response = window.Android[action](...data)
            return response
          }
        }
      } catch (error) {
        this.emit(EVENT_ERROR, {
          type: 'json parse error in dispatch native bridge',
          error
        })
      }
    }
    return undefined
  }

  asyncNativeBridge (action, data, responseCallback) { // 异步方式与native交互适用于Android&&IOS
    if (!this.isApp && !ISTEST) {
      return
    }
    let { uniqueId } = this
    const {
      responseCallbacks
    } = this
    const message = { action }

    if (!isUndefined(data) && !isFunction(data)) {
      message.data = data
    }

    if (isFunction(data)) {
      responseCallback = data
    }

    if (window.Flutter && window.Flutter.postMessage) {
      if (responseCallback) {
        const callbackId = `cb_${uniqueId++}_${new Date().getTime()}_${getNewVisitorUUID()}`
        responseCallbacks[callbackId] = responseCallback
        message.callbackId = callbackId
      }
      window.Flutter.postMessage(JSON.stringify(message))
    } else if (window.Android && window.Android.postMessage) {
      if (responseCallback) {
        const callbackId = `cb_${uniqueId++}_${new Date().getTime()}_${getNewVisitorUUID()}`
        responseCallbacks[callbackId] = responseCallback
        message.callbackId = callbackId
      }
      window.Android.postMessage(JSON.stringify(message))
    }
  }
}
